import React, { useState } from 'react';
import PropTypes from 'prop-types';

import UTRModal from '../../../Modal';
import SavedListModalActions from '../../SavedListModalActions';
import clampToMaxCodeUnits from '../../../../utils/clampToMaxCodeUnits';
import { MAX_NOTE_LENGTH, NOTE_MODAL_TITLE, NOTE_PLACEHOLDER, CLEAR_NOTE_LABEL } from './constants';

import styles from './playerNoteModal.scss';

// The ticket's data-testid contract pins this exact literal (no `modal.` namespace), matching the
// sibling SaveToListModal / RemovePlayerConfirmModal — the web verifier's Playwright selectors depend
// on it. Intentional deviation from CLAUDE.md rule 4; don't "fix" it to the general convention.
const TESTID_PREFIX = 'add-note-modal';

// Add/Edit Player Note modal (TMS-2102): title + Clear Note link, a textarea pre-filled with the row's
// current note, and a centered Cancel/Save footer. Clear only empties the textarea locally — the cleared
// (empty) state is committed via Save like any other edit, and Cancel discards it. Save is enabled only
// when the text differs from the saved note (an empty save deletes the note). The parent owns the write,
// the busy state, and the targeted row patch — this component only edits text and reports Save/Cancel.
const PlayerNoteModal = ({ initialNote, isSaving, onSave, onCancel }) => {
    const [text, setText] = useState(initialNote);

    // Gate on unsaved changes, not on non-blank: clearing an existing note to empty is a real change to
    // commit (Save then deletes it), while re-typing the saved value back is not. Trim both sides so
    // whitespace-only edits don't count — the note is stored trimmed.
    const isSaveDisabled = text.trim() === initialNote.trim() || isSaving;

    const handleSave = () => {
        if (isSaveDisabled) {
            return;
        }
        // Persist the trimmed value; an empty result deletes the note (the parent handles the delete).
        onSave(text.trim());
    };

    return (
        <UTRModal
            title={NOTE_MODAL_TITLE}
            onCloseClick={onCancel}
            className={styles.playerNoteModal}
            data-testid={TESTID_PREFIX}
        >
            <div className={styles.header}>
                <div className={`${styles.desktopTitle} d-none d-md-block`} data-testid={`${TESTID_PREFIX}.desktop-title`}>
                    {NOTE_MODAL_TITLE}
                </div>
                <button
                    type="button"
                    className={styles.clearLink}
                    onClick={() => setText('')}
                    disabled={isSaving || text.length === 0}
                    data-testid={`${TESTID_PREFIX}.clear`}
                >
                    <i className={`far fa-times-circle ${styles.clearIcon}`} />
                    {CLEAR_NOTE_LABEL}
                </button>
            </div>
            <textarea
                className={styles.textarea}
                value={text}
                onChange={event => setText(clampToMaxCodeUnits(event.target.value, MAX_NOTE_LENGTH))}
                placeholder={NOTE_PLACEHOLDER}
                data-testid={`${TESTID_PREFIX}.textarea`}
            />
            {/*
              Code units, not whole characters like the name counter: the note API caps on
              note.Trim().Length (code units), so the reading must match what the server measures.
            */}
            <div className={styles.charCounter} data-testid={`${TESTID_PREFIX}.char-counter`}>
                {`${text.length}/${MAX_NOTE_LENGTH}`}
            </div>
            <SavedListModalActions
                data-testid={TESTID_PREFIX}
                onCancel={onCancel}
                onSave={handleSave}
                saveDisabled={isSaveDisabled}
                cancelTestidSuffix="cancel"
                primaryTestidSuffix="save"
            />
        </UTRModal>
    );
};

PlayerNoteModal.propTypes = {
    initialNote: PropTypes.string,
    isSaving: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

PlayerNoteModal.defaultProps = {
    initialNote: '',
    isSaving: false,
};

export default PlayerNoteModal;
